import { useEffect } from 'react';
import { restoredInputKeys } from '../utils/liveEvents';
import { contentKeys } from './useContent';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { snapshotsApi, type AbandonSnapshotRestore, type ApplySnapshotRestore, type UpdateSnapshotRetention, type SnapshotPageParams } from '../api/snapshots';

export const snapshotKeys = {
  all: ['snapshots'] as const,
  site: (siteSlug: string) => ['snapshots', siteSlug] as const,
  restore: (siteSlug: string, id: string) => [...snapshotKeys.site(siteSlug), 'restore', id] as const,
  retention: (siteSlug: string) => [...snapshotKeys.site(siteSlug), 'retention'] as const,
  list: (siteSlug: string, params?: SnapshotPageParams) => [...snapshotKeys.site(siteSlug), 'list', params] as const,
  detail: (siteSlug: string, id: string, params?: SnapshotPageParams) => [...snapshotKeys.site(siteSlug), 'detail', id, params] as const,
  entry: (siteSlug: string, id: string, key: string, params?: SnapshotPageParams) => [...snapshotKeys.site(siteSlug), 'entry', id, key, params] as const,
  compare: (siteSlug: string, from: string, to: string, params?: SnapshotPageParams) => [...snapshotKeys.site(siteSlug), 'compare', from, to, params] as const,
};
export function useSnapshots(siteSlug: string, params?: SnapshotPageParams) {
  return useQuery({ queryKey: snapshotKeys.list(siteSlug, params), queryFn: () => snapshotsApi.list(siteSlug, params), enabled: !!siteSlug });
}
export function useSnapshot(siteSlug: string, id: string, params?: SnapshotPageParams) {
  return useQuery({ queryKey: snapshotKeys.detail(siteSlug, id, params), queryFn: () => snapshotsApi.get(siteSlug, id, params), enabled: !!siteSlug && !!id });
}
export function useSnapshotEntry(siteSlug: string, id: string, key: string, params?: SnapshotPageParams) {
  return useQuery({ queryKey: snapshotKeys.entry(siteSlug, id, key, params), queryFn: () => snapshotsApi.entry(siteSlug, id, key, params), enabled: !!siteSlug && !!id && !!key });
}
export function useSnapshotComparison(siteSlug: string, from: string, to: string, params?: SnapshotPageParams) {
  return useQuery({ queryKey: snapshotKeys.compare(siteSlug, from, to, params), queryFn: () => snapshotsApi.compare(siteSlug, from, to, params), enabled: !!siteSlug && !!from && !!to });
}

export function useSnapshotRetention(siteSlug: string) {
  return useQuery({ queryKey: snapshotKeys.retention(siteSlug), queryFn: () => snapshotsApi.retention(siteSlug), enabled: !!siteSlug });
}
export function usePreviewSnapshotRetention(siteSlug: string) {
  return useMutation({ mutationFn: (limit: number) => snapshotsApi.previewRetention(siteSlug, limit), retry: false });
}
export function useUpdateSnapshotRetention(siteSlug: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSnapshotRetention) => snapshotsApi.updateRetention(siteSlug, data), retry: false,
    onSuccess: (response) => {
      client.setQueryData(snapshotKeys.retention(siteSlug), response);
      void client.invalidateQueries({ queryKey: snapshotKeys.site(siteSlug) });
      void client.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
    },
    onError: () => {
      // Pruning may have committed before the cap-setting response failed.
      void client.invalidateQueries({ queryKey: snapshotKeys.site(siteSlug) });
      void client.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
    },
  });
}

export function useSnapshotRestoreStatus(siteSlug: string, operationId: string) {
  const client = useQueryClient();
  const query = useQuery({ queryKey: snapshotKeys.restore(siteSlug, operationId), queryFn: () => snapshotsApi.restoreStatus(siteSlug, operationId), enabled: !!siteSlug && !!operationId, retry: false,
    refetchInterval: (query) => query.state.status !== 'error' && query.state.data?.data && !query.state.data.data.completed ? 5000 : false });
  const completed = query.data?.success && query.data.data?.completed;
  useEffect(() => { if (completed) invalidateRestoredInputs(client, siteSlug); }, [client, siteSlug, operationId, completed]);
  return query;
}
export function usePreviewSnapshotRestore(siteSlug: string) {
  return useMutation({ mutationFn: ({ snapshotId, operationId }: { snapshotId: string; operationId: string }) => snapshotsApi.restorePreflight(siteSlug, snapshotId, operationId), retry: false });
}
export function useApplySnapshotRestore(siteSlug: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: ({ snapshotId, ...data }: ApplySnapshotRestore & { snapshotId: string }) => snapshotsApi.restore(siteSlug, snapshotId, data), retry: false,
    onSettled: () => {
      // A failed response may follow committed work. Re-read, never optimistically restore.
      invalidateRestoredInputs(client, siteSlug);
    },
  });
}

export function useActiveSnapshotRestore(siteSlug: string, enabled = true) {
  return useQuery({ queryKey: snapshotKeys.restore(siteSlug, 'active'), queryFn: () => snapshotsApi.activeRestore(siteSlug), enabled: !!siteSlug && enabled, retry: false });
}

function invalidateRestoredInputs(client: ReturnType<typeof useQueryClient>, siteSlug: string) {
  for (const key of restoredInputKeys(siteSlug)) void client.invalidateQueries({ queryKey: key });
}

export function usePreviewRestoreAbandonment(siteSlug: string) {
  return useMutation({ mutationFn: (operationId: string) => snapshotsApi.abandonPreflight(siteSlug, operationId), retry: false });
}
export function useAbandonSnapshotRestore(siteSlug: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: ({ operationId, ...data }: AbandonSnapshotRestore & { operationId: string }) => snapshotsApi.abandonRestore(siteSlug, operationId, data), retry: false,
    onSettled: () => invalidateRestoredInputs(client, siteSlug),
  });
}
